/**
 * JWT Minting for Operator UI
 *
 * Uses jose library for browser-compatible JWT signing.
 * Matches harness JWT format exactly:
 * - sub: 'operator-ui'
 * - tenant_id: from config
 * - iat: issued at
 * - exp: expiration (1 hour)
 */

import * as jose from 'jose';

export interface JwtConfig {
  tenantId: string;
  secret: string;
  actorId?: string;
}

/**
 * Mints a JWT token for API authentication.
 * Token is valid for 1 hour.
 */
export async function mintJwt(config: JwtConfig): Promise<string> {
  const now = Math.floor(Date.now() / 1000);
  const secret = new TextEncoder().encode(config.secret);

  const jwt = await new jose.SignJWT({
    sub: config.actorId ?? 'operator-ui',
    tenant_id: config.tenantId,
  })
    .setProtectedHeader({ alg: 'HS256' })
    .setIssuedAt(now)
    .setExpirationTime(now + 3600)
    .sign(secret);

  return jwt;
}

/**
 * Checks if a JWT is expired or will expire within the buffer time.
 * Returns true if the token should be refreshed.
 */
export function isTokenExpired(token: string, bufferSeconds = 300): boolean {
  try {
    const payload = jose.decodeJwt(token);
    if (typeof payload.exp !== 'number') {
      return true;
    }
    const now = Math.floor(Date.now() / 1000);
    return payload.exp - bufferSeconds <= now;
  } catch {
    return true;
  }
}

// Learner tokens are now minted server-side via POST /assessments/:id/learner-token
// This ensures tokens are scoped to a specific assessment and properly audited
